import { Button, DataTable, Drawer, EmptyState, Icon } from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { planColumns } from './columns.tsx';
import type { PlanRow } from './types.ts';

/**
 * The commission rate card.
 *
 * Live plans grouped by SHAPE, not by affiliate: a plan is dated
 * (`valid_from` / `valid_to`) precisely so that changing a model applies to
 * future cohorts only, and the live grouping is what a new cohort would be
 * acquired under. Two affiliates on identical terms are one row here, which is
 * why the "New model" action has to name an affiliate before it can do
 * anything — there is no model entity to create on its own.
 */
export function CommissionModelsDrawer({
  open,
  onClose,
  plans,
  loading,
  failed,
  affiliateCount,
  ledgerFailed,
  brandLabel,
  canWrite,
  onNewModel,
}: {
  open: boolean;
  onClose: () => void;
  plans: PlanRow[];
  loading: boolean;
  failed: boolean;
  affiliateCount: number;
  ledgerFailed: boolean;
  brandLabel: string | null;
  canWrite: boolean;
  onNewModel: () => void;
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Affiliate commission models"
      subtitle={
        loading
          ? 'Loading the rate card…'
          : failed
            ? 'the rate card could not be read'
            : `${plans.length} live plans across ${
                ledgerFailed
                  ? 'a network that could not be counted'
                  : `${formatCount(affiliateCount)} affiliates`
              }`
      }
      actions={
        /* Hidden, not disabled, for a role that cannot use it. A control that
           advertises a capability the user does not hold is the pattern this
           console avoids everywhere else. */
        canWrite ? (
          <Button variant="primary" onClick={onNewModel}>
            <Icon name="plus" />
            Set a model
          </Button>
        ) : null
      }
    >
      <div className="stack">
        <DataTable
          caption="Live affiliate commission plans with their type, terms and the number of affiliates on each"
          columns={planColumns}
          rows={plans}
          rowKey={(p) => `${p.model}:${p.revshare_bp ?? ''}:${p.cpa_amount ?? ''}:${p.cpa_hold_days}:${p.cpa_min_deposit ?? ''}`}
          empty={
            failed ? (
              /* Its own flag, not the ledger's: the rate card is a separate GET
                 and can fail while the table behind it is fine, or arrive intact
                 while the table behind it is not. */
              <EmptyState icon="warning-circle" title="The rate card could not be read">
                The rate card could not be fetched, so no plan is listed because none was read —
                not because the network has no commission basis. The message that opened carries
                the reason; try again shortly.
              </EmptyState>
            ) : (
              <EmptyState icon="receipt" title="No live commission plans">
                {/* No rate-card row with valid_to IS NULL. */}
                The rate card holds no plan that is still in force. Nothing on the network has a
                commission basis.
              </EmptyState>
            )
          }
        />

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Attribution is last-click, within the window set by{' '}
            the attribution window. Changing a model closes the live plan
            and starts a new one from that moment, so it affects future cohorts only — every player
            already attributed keeps the plan they were acquired under.
          </div>
        </div>

        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            This list is <strong>not narrowed by brand</strong>. It groups every live plan on the
            platform, so the affiliate counts here can exceed the {brandLabel ?? 'list'} above. Plans are grouped by
            shape, not by identity — two affiliates on identical terms collapse into one row, which
            is why setting a model asks which affiliate it applies to.
          </div>
        </div>
      </div>
    </Drawer>
  );
}
